import { supabase } from '@/lib/supabase';
import type {
  Project,
  ProductDetails,
  ProductCopy,
  ProductPage,
  ProductDeploy,
  CompleteProject
} from '@/features/projects/types';

export async function getCompleteProject(projectId: string): Promise<CompleteProject | null> {
  // Get project
  const { data: project, error: projError } = await supabase
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .single();

  if (projError || !project) {
    console.error('Project not found:', projError);
    return null;
  }

  // Get product_details
  const { data: details } = await supabase
    .from('product_details')
    .select('*')
    .eq('project_id', projectId)
    .maybeSingle();

  // Get product_copy
  const { data: copy } = await supabase
    .from('product_copy')
    .select('*')
    .eq('project_id', projectId)
    .maybeSingle();

  // Get product_page
  const { data: page } = await supabase
    .from('product_page')
    .select('*')
    .eq('project_id', projectId)
    .maybeSingle();

  // Get product_deploy
  const { data: deploy } = await supabase
    .from('product_deploy')
    .select('*')
    .eq('project_id', projectId)
    .maybeSingle();

  return {
    project: project as Project,
    details: details as ProductDetails | null,
    copy: copy as ProductCopy | null,
    page: page as ProductPage | null,
    deploy: deploy as ProductDeploy | null
  };
}

export async function getUserProjects(userId: string, options?: { status?: string; sortBy?: string; search?: string }): Promise<Project[]> {
  let query = supabase
    .from('projects')
    .select('*')
    .eq('user_id', userId);

  if (options?.status && options.status !== 'all') {
    query = query.eq('status', options.status);
  }

  if (options?.search) {
    query = query.ilike('name', `%${options.search}%`);
  }

  if (options?.sortBy === 'name') {
    query = query.order('name', { ascending: true });
  } else if (options?.sortBy === 'views') {
    query = query.order('views', { ascending: false });
  } else {
    query = query.order('created_at', { ascending: false });
  }

  const { data, error } = await query;
  if (error) throw error;
  return data as Project[];
}

export async function getShowcaseProjects(options?: { limit?: number; sortBy?: 'newest' | 'popular' }): Promise<Project[]> {
  let query = supabase
    .from('projects')
    .select('*, product_details(product_name, description)') // Join with details to get description
    .eq('published_to_showcase', true)
    .eq('status', 'published'); // Ensure it's actually published

  if (options?.sortBy === 'popular') {
    query = query.order('views', { ascending: false });
  } else {
    query = query.order('showcase_publish_date', { ascending: false }); // Default newest
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data as any[]; // Type assertion needed due to join
}

export async function getProjectStats(userId: string) {
  const { data: projects } = await supabase
    .from('projects')
    .select('status, views, conversions')
    .eq('user_id', userId);

  if (!projects) {
    return {
      total_pages: 0,
      active_pages: 0,
      total_views: 0,
      total_conversions: 0
    };
  }

  return {
    total_pages: projects.length,
    active_pages: projects.filter(p => p.status === 'published').length,
    total_views: projects.reduce((sum, p) => sum + (p.views || 0), 0),
    total_conversions: projects.reduce((sum, p) => sum + (p.conversions || 0), 0)
  };
}
